/** Shared fail-closed boundary for measurement on the public website. */
export function readTrackingConsent(purpose: 'analytics' | 'marketing'): boolean {
  try {
    const date = Date.parse(localStorage.getItem('cookie_consent_date') ?? '');
    if (!Number.isFinite(date) || Date.now() - date > 180 * 24 * 60 * 60 * 1000) return false;
    const consent: unknown = JSON.parse(localStorage.getItem('cookie_consent') ?? 'null');
    return !!consent && typeof consent === 'object' && (consent as Record<string, unknown>)[purpose] === true;
  } catch { return false; }
}

export function isSensitiveTrackingPath(path: string): boolean {
  return /hiv-positive|hiv\+|hair-analysis|haar-analyse|candidacy|patient|dashboard|admin|auth|reset-password/i.test(path);
}

export function canMeasure(purpose: 'analytics' | 'marketing'): boolean {
  return typeof window !== 'undefined' && !isSensitiveTrackingPath(window.location.pathname) && readTrackingConsent(purpose);
}

// No free text, patient identifiers, treatment choices or contact targets.
const SAFE_KEYS = new Set([
  'event', 'page_path', 'page_location', 'page_title', 'language', 'query_type', 'channel_group',
  'form_name', 'form_location', 'form_type', 'conversion_type', 'conversion_value', 'currency',
  'click_location', 'button_name', 'destination', 'channel', 'outbound', 'scroll_percentage',
  'time_on_page', 'time_on_site', 'engagement_time', 'form_started', 'form_completed',
  'duplicate', 'transaction_id', 'exit_intent_action',
]);
export function sanitizeMeasurement(payload: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(payload).filter(([key, value]) => {
    if (!SAFE_KEYS.has(key) || value == null || typeof value === 'object') return false;
    if (typeof value !== 'string') return true;
    if (value.includes('@') || /\d[\s().+-]*\d[\s().+-]*\d[\s().+-]*\d[\s().+-]*\d[\s().+-]*\d[\s().+-]*\d/.test(value)) return false;
    return !isSensitiveTrackingPath(value);
  }).map(([key, value]) => [key, typeof value === 'string' ? value.split(/[?#]/)[0].slice(0, 160) : value]));
}